import {FormikHelpers} from 'formik/dist/types';
import {useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {
  selectClinicData,
  setClinicData,
} from '@/src/app/store/clinic/clinicDataSlice';
import useUserActions from '@/src/app/store/user/useUserActions';
import PopupContext from '@/src/popup/utils/PopupContext';
import ClinicInfoSettingsLayout from '@/src/profile/accountSettings/components/ClinicInfoSettingsLayout';
import EditClinicInfoPopupLayout from '@/src/profile/accountSettings/components/EditClinicInfoPopupLayout';
import {CLINIC_INFO_KEYS} from '@/src/profile/accountSettings/constants/clinicInfokeys';
import {ClinicInfoSettingsTypes} from '@/src/profile/accountSettings/containers/types/ClinicInfoSettingsTypes';
import requestUpdateClinicInfo from '@/src/profile/accountSettings/utils/requestUpdateClinicInfo';
import TextLocal from '@/src/textLocal/containers/TextLocal';
import PageTitle from '@/src/ui/pageTitle/components/PageTitle';

import css from '../styles/ClinicInfoSettingsLayout.module.css';

const ClinicInfoSettings = (): JSX.Element => {
  const {clinicData} = useSelector(selectClinicData);
  const {requestUserData} = useUserActions();
  const dispatch = useDispatch();

  const {openPopup, closePopup} = useContext(PopupContext);

  const handleUpdateClinicInfo = async (
    values: ClinicInfoSettingsTypes,
    {setFieldError}: FormikHelpers<any>
  ): Promise<void> => {
    const {status, responseBody} = await requestUpdateClinicInfo(
      clinicData.id,
      values
    );

    if (status === RESPONSE_STATUSES.SUCCESS) {
      dispatch(
        setClinicData({
          clinicData: responseBody,
          isFetched: true,
        })
      );

      await requestUserData();

      closePopup();
    }

    if (
      responseBody &&
      responseBody.error &&
      typeof responseBody.error === 'object'
    ) {
      for (const key in responseBody.error) {
        if (Object.prototype.hasOwnProperty.call(responseBody.error, key)) {
          setFieldError(CLINIC_INFO_KEYS[key], responseBody.error[key]);
        }
      }
    }
  };

  const handleChangeClinicInfoClick = useCallback((): void => {
    // @ts-ignore
    openPopup({
      popup: (
        <EditClinicInfoPopupLayout
          clinicName={clinicData.organization_name}
          email={clinicData.email}
          userName={clinicData.slug}
          website={clinicData.website}
          onSubmitClick={handleUpdateClinicInfo}
          onCancelClick={closePopup}
        />
      ),
      options: {withCloseButton: true},
    });
  }, [clinicData]);

  return (
    <div className={css.clinicSettings}>
      <PageTitle
        tag={PageTitle.TAG.H3}
        title={<TextLocal id={'profile.clinic-info'} />}
        offset={PageTitle.BOTTOM_OFFSET.OFFSET_15}
      />
      <ClinicInfoSettingsLayout
        clinicName={clinicData.organization_name}
        email={clinicData.email}
        userName={clinicData.slug}
        website={clinicData.website}
        onEditClinicInfoClick={handleChangeClinicInfoClick}
      />
    </div>
  );
};

export default ClinicInfoSettings;
